import { ApiResponse, GlobalStore } from '@roc/feature-app-core';
import { action, flow, makeObservable, observable, toJS } from 'mobx';
import { ValuationDisputeService } from '../../services/documentForms/valuationDisputeService';

export const COMPARABLE_CONDITION_OPTIONS = [
  { label: '--', value: '' },
  { label: 'Excellent', value: 'Excellent' },
  { label: 'Good', value: 'Good' },
  { label: 'Average', value: 'Average' },
  { label: 'Fair', value: 'Fair' },
  { label: 'Poor', value: 'Poor' },
];

export interface Comparable {
  address: string;
  cityStateZip: string;
  closedLeaseDate: string;
  mlsDataSource: string;
  salesPriceRentAmount: string;
  proxToSubject: string;
  aboveGradeSqFt: string;
  belowGradeSqFt: string;
  age: string;
  propertyCondition: string;
  bedBathCount: string;
  lotSize: string;
  specialAmenities: string;
}

export interface ValuationDisputeFormData {
  propertyAddress: string;
  effectiveDate: string;
  appraisedValue: string;
  internalValue: string;
  salesNotRepresentative: boolean;
  incorrectPropertyInformation: boolean;
  incorrectPropertyInformationDetails: string;
  other: boolean;
  otherDetails: string;
  subject: Comparable;
  comparables: Comparable[];
  clientComments: string;
  reviewerComments: string;
}

const emptyComparable = (): Comparable => ({
  address: '',
  cityStateZip: '',
  closedLeaseDate: '',
  mlsDataSource: '',
  salesPriceRentAmount: '',
  proxToSubject: '',
  aboveGradeSqFt: '',
  belowGradeSqFt: '',
  age: '',
  propertyCondition: '',
  bedBathCount: '',
  lotSize: '',
  specialAmenities: '',
});

const defaultFormData = (): ValuationDisputeFormData => ({
  propertyAddress: '',
  effectiveDate: '',
  appraisedValue: '',
  internalValue: '',
  salesNotRepresentative: false,
  incorrectPropertyInformation: false,
  incorrectPropertyInformationDetails: '',
  other: false,
  otherDetails: '',
  subject: emptyComparable(),
  comparables: [emptyComparable(), emptyComparable(), emptyComparable(), emptyComparable()],
  clientComments: '',
  reviewerComments: '',
});

export class ValuationDisputeStore {
  private globalStore: GlobalStore;
  private valuationDisputeService: ValuationDisputeService;

  formData: ValuationDisputeFormData;
  currentStep: number;
  isLoading: boolean;
  isSaving: boolean;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.valuationDisputeService = new ValuationDisputeService();
    this.setDefaults();
    makeObservable(this, {
      formData: observable,
      currentStep: observable,
      isLoading: observable,
      isSaving: observable,
      initialize: flow,
      saveForm: flow,
      setCurrentStep: action,
      setField: action,
      setComparableField: action,
      reset: action,
    });
  }

  setDefaults() {
    this.formData = defaultFormData();
    this.currentStep = 0;
    this.isLoading = false;
    this.isSaving = false;
  }

  reset() {
    this.setDefaults();
  }

  setCurrentStep(step: number) {
    this.currentStep = step;
  }

  setField<K extends keyof ValuationDisputeFormData>(
    field: K,
    value: ValuationDisputeFormData[K]
  ) {
    this.formData[field] = value;
  }

  setComparableField(
    column: 'subject' | number,
    field: keyof Comparable,
    value: string
  ) {
    if (column === 'subject') {
      this.formData.subject[field] = value;
    } else {
      this.formData.comparables[column][field] = value;
    }
  }

  *initialize(loanId: number, propertyId: number, taskName: string, collateralAddress?: string) {
    this.reset();
    this.isLoading = true;
    try {
      const response: ApiResponse = yield this.valuationDisputeService.getDisputeForm(
        loanId,
        propertyId,
        taskName
      );
      const rawData = response?.data?.data;
      const data: ValuationDisputeFormData | null = rawData
        ? (typeof rawData === 'string' ? JSON.parse(rawData) : rawData)
        : null;
      if (data) {
        this.formData = {
          ...defaultFormData(),
          ...data,
          propertyAddress: data.propertyAddress || collateralAddress || '',
          subject: { ...emptyComparable(), ...(data.subject ?? {}) },
          comparables: Array.from({ length: 4 }, (_, i) => ({
            ...emptyComparable(),
            ...(data.comparables?.[i] ?? {}),
          })),
        };
      } else if (collateralAddress) {
        this.formData.propertyAddress = collateralAddress;
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while loading the dispute form.',
      });
    } finally {
      this.isLoading = false;
    }
  }

  *saveForm(taskId: number, loanId: number, propertyId: number, taskName: string, onClose?: () => void) {
    this.isSaving = true;
    try {
      yield this.valuationDisputeService.saveDisputeForm(
        taskId,
        loanId,
        propertyId,
        taskName,
        JSON.stringify(toJS(this.formData))
      );
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Dispute form submitted successfully.',
      });
      if (onClose) {
        onClose();
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while submitting the dispute form.',
      });
    } finally {
      this.isSaving = false;
    }
  }
}

export default ValuationDisputeStore;
